import {
  getGroupKeysForRow,
  TAG_UNTAGGED_KEY,
  type CompareBy,
} from "@/run-detail/compare-by"

import type { RunRowLike } from "@/types/RunRow"

const TEST_CASE_LABEL_MAX_LENGTH = 60

export type ReportRunsById = Record<string, string>

export type ReportEntityDisplayMap = Record<
  string,
  { display_name?: string | null }
>

export interface BuildCompareByDisplayLabelsParams {
  agentsById: ReportEntityDisplayMap | null | undefined
  compareByConfigForGroup: CompareBy | null
  preferTestCaseDescription?: boolean
  personasById: ReportEntityDisplayMap | null | undefined
  runRows: RunRowLike[] | null | undefined
  runsById: ReportRunsById
}

export function buildSimulationOutputRunIdMap(
  runRows: RunRowLike[] | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>()

  if (!runRows) {
    return map
  }

  for (const row of runRows) {
    map.set(row.simulation_output_id, row.run_id)
  }

  return map
}

export function getPresentMetricIds(
  runRows: RunRowLike[] | null | undefined,
): string[] {
  const ids = new Set<string>()

  if (!runRows) {
    return []
  }

  for (const row of runRows) {
    for (const id of Object.keys(row.metric_outputs ?? {})) {
      ids.add(id)
    }
  }

  return Array.from(ids)
}

export function buildCompareByDisplayLabels({
  agentsById,
  compareByConfigForGroup,
  personasById,
  preferTestCaseDescription = true,
  runRows,
  runsById,
}: BuildCompareByDisplayLabelsParams): Record<string, string> {
  if (!compareByConfigForGroup || !runRows?.length) {
    return {}
  }

  const labels: Record<string, string> = {}

  for (const row of runRows) {
    for (const key of getGroupKeysForRow(row, compareByConfigForGroup)) {
      if (key in labels) {
        continue
      }

      if (compareByConfigForGroup.dimension === "run" && runsById[key]) {
        labels[key] = runsById[key]
        continue
      }

      if (
        compareByConfigForGroup.dimension === "agent" &&
        agentsById?.[key]?.display_name
      ) {
        labels[key] = agentsById[key].display_name
        continue
      }

      if (compareByConfigForGroup.dimension === "mutation") {
        labels[key] = row.mutation_name ?? key
        continue
      }

      if (
        compareByConfigForGroup.dimension === "persona" &&
        personasById?.[key]?.display_name
      ) {
        labels[key] = personasById[key].display_name
        continue
      }

      if (compareByConfigForGroup.dimension === "test_case") {
        const fullLabel = preferTestCaseDescription
          ? ((row.test_case_description?.trim() || row.input_str?.trim()) ?? "")
          : (row.input_str?.trim() ?? "")
        labels[key] =
          fullLabel.length <= TEST_CASE_LABEL_MAX_LENGTH
            ? fullLabel
            : `${fullLabel.slice(0, TEST_CASE_LABEL_MAX_LENGTH)}…`
        continue
      }

      if (compareByConfigForGroup.dimension === "tag") {
        // Tag keys are just the tag name; the untagged sentinel gets a
        // human-readable label so it renders as "Untagged" instead of
        // an internal sentinel identifier.
        labels[key] = key === TAG_UNTAGGED_KEY ? "Untagged" : key
        continue
      }

      labels[key] = key
    }
  }

  return labels
}
